<template>
  <Dialog trigger-classes="focus:outline-none">
    <template #trigger>
      <button v-if="images.length" class="flex items-center flex-row-reverse" style="margin-left: -25px">
        <div v-for="(image, index) in images.slice(0, 3)" :key="image.meta.id" class="!h-12 sm:!mb-2" :class="buttonClasses(index)">
          <ImageButton v-if="image.previewResult.state === ProcessingState.Error || image.original.state === UploadState.Error" :selected="store.selectedImage === image" class="relative">
            <div class="bg-typo text-white flex justify-center items-center bg-opacity-80 absolute w-full h-full">
              <WarningIcon />
            </div>
            <HTMLImage :src="image.meta.preview" img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard" />
          </ImageButton>

          <ImageButton v-else-if="image.previewResult.state === ProcessingState.Processing" :selected="store.selectedImage === image" class="relative animate-bounceSmall">
            <div class="bg-typo flex justify-center items-center bg-opacity-80 absolute w-full h-full"></div>
            <HTMLImage :src="image.meta.preview" img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard" />
          </ImageButton>

          <ImageButton v-else :selected="store.selectedImage === image">
            <HTMLImage :src="image.meta.preview" img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard" />
          </ImageButton>
        </div>
      </button>
    </template>
    <template v-slot:content="{ closeDialog }">
      <div class="w-full grid gap-4 grid-cols-2 mt-4">
        <ImageButton @click="uploadFile" style="aspect-ratio: 1" class="!border-secondary-hover !shadow-sm sm:!m-2 sm:!mb-0 !w-full h-full">
          <div class="bg-secondary-hover h-full flex justify-center items-center">
            <AddIcon />
          </div>
        </ImageButton>
        <div v-for="image in images" class="relative w-full h-full" style="aspect-ratio: 1">
          <ImageButton class="w-full h-full relative" style="aspect-ratio: 1" :key="image.meta.id" :selected="store.selectedImage === image" @click="select($event, image, closeDialog)">
            <div v-if="image.previewResult.state === ProcessingState.Error || image.original.state === UploadState.Error" class="bg-typo text-white flex justify-center items-center bg-opacity-80 absolute w-full h-full">
              <WarningIcon />
            </div>
            <HTMLImage :src="image.meta.preview" class="h-full" img-classes="object-cover bg-white checkerboard !h-full" />
          </ImageButton>
          <div class="trash absolute top-0 right-0" style="width: 33%; aspect-ratio: 1;%">
            <div class="trash rounded-full bg-white absolute top-0 right-0 m-1">
              <IconButton v-if="!requiresDeleteConfirmation(image)" variant="primary" :title="I18n.t('editor.delete_selected')" @click.prevent="() => deleteImage(image)">
                <TrashIcon />
              </IconButton>
              <Dialog v-else trigger-classes="focus:outline-none">
                <template #trigger>
                  <IconButton variant="secondary">
                    <TrashIcon />
                  </IconButton>
                </template>
                <template v-slot:content="{ closeDialog }">
                  <div class="flex flex-col gap-y-6 w-full max-h-[680px]">
                    <Heading level="h4">{{ I18n.t("editor.are_you_sure") }}</Heading>
                    <p>{{ I18n.t("editor.you_purchased") }}</p>
                    <div class="sm:flex sm:flex-row-reverse sm:gap-x-3 space-y-3 sm:space-y-0 grow">
                      <Button fullWidth class="mb-2" type="submit" @click.prevent="() => deleteImage(image, closeDialog)">{{ I18n.t("editor.delete") }}</Button>
                      <Button fullWidth autofocus variant="secondary" type="reset" @click="closeDialog">{{ I18n.t("editor.cancel") }}</Button>
                    </div>
                  </div>
                </template>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { Dialog, ImageButton, Image as HTMLImage, WarningIcon, AddIcon, TrashIcon, IconButton, Button, Heading } from "prism";
import { ProcessingState, Image, UploadState } from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { rbgRemoveImageV100 } from "kaleido-event-tracker";

import { useEditorStore } from "@/stores/editor_store";
const store = useEditorStore();

const images = computed(() => {
  const selectedImage = store.selectedImage;
  const otherImages = store.images.filter((image) => image !== selectedImage);
  const images = [selectedImage, ...otherImages].filter((image) => image !== null);

  return images;
});

const select = (event: any, image: Image, closeDialog: () => void) => {
  store.selectImage(image);
  closeDialog();
};

const deleteImage = (image: Image, closeDialog?: () => void) => {
  Client.deleteImage(image);
  store.deleteImage(image);
  if (closeDialog) closeDialog();
  window.track("Images", "remove_result", "Remove result");
  rbgRemoveImageV100({ image_id: image.meta.id });
};

const requiresDeleteConfirmation = (image: Image) => {
  const isHdResultPresent = image.hdResult !== undefined;
  const isHdResultReady = image.hdResult?.state === ProcessingState.Ready;

  return isHdResultPresent && !isHdResultReady;
};

const buttonClasses = (i: number) => {
  switch (i) {
    case 0:
      return "z-20";
    case 1:
      return "z-10 scale-95 translate-x-5";
    case 2:
      return "scale-75 translate-x-10";
    default:
      return "";
  }
};

const uploadFile = () => {
  window.onUploadFile();
};
</script>
